import { useEffect, useState, useRef } from "react";
import { useDebounce } from "@/src/hooks/use-debounce";
import { logger } from "@/src/lib/logging/logger";
import { ICD10 } from "@/src/models/icd10";
export const useICD10Search = (searchTerm: string) => {
  const [results, setResults] = useState<ICD10[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const currentControllerRef = useRef<AbortController | null>(null);
  const debouncedSearchTerm = useDebounce(searchTerm, 100);
  useEffect(() => {
    // Cancel any existing request
    if (currentControllerRef.current) {
      currentControllerRef.current.abort();
    }
    setError(null);
    if (!debouncedSearchTerm.trim()) {
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const controller = new AbortController();
    currentControllerRef.current = controller;
    const signal = controller.signal;
    const searchICD10 = async () => {
      try {
        // Check if request was aborted before making the call
        if (signal.aborted) {
          return;
        }
        const response = await fetch(`/api/icd10/search?q=${encodeURIComponent(debouncedSearchTerm)}&limit=50`, {
          signal
        });

        // Check again after the fetch
        if (signal.aborted) {
          return;
        }
        if (!response.ok) {
          logger.error("ICD-10 search failed", {
            status: response.status,
            statusText: response.statusText,
            query: debouncedSearchTerm
          });
          throw new Error("Failed to search ICD-10 codes");
        }
        const searchResults = await response.json();

        // Final check before setting results
        if (!signal.aborted) {
          setResults(searchResults);
        }
      } catch (err) {
        if (err instanceof Error && err.name === "AbortError") {
          return; // Don't update state for aborted requests
        }
        logger.error("ICD-10 search error", {
          error: err,
          query: debouncedSearchTerm
        });
        const errorMessage = err instanceof Error ? err.message : "Failed to search ICD-10 codes";
        setError(errorMessage);
        setResults([]);
      } finally {
        // Only update loading state if this request wasn't aborted
        if (!signal.aborted) {
          setLoading(false);
        }
      }
    };
    searchICD10();
    return () => {
      if (currentControllerRef.current) {
        currentControllerRef.current.abort();
      }
    };
  }, [debouncedSearchTerm]);
  return {
    results,
    loading,
    error
  };
};